import Select, { ISelect } from '@/components/form/select';
import { useCommonList } from '@/hooks';
import { isMobile } from '@/utils/mobile.util';
import { memo, useCallback, useMemo } from 'react';

import { DefaultOptionType } from 'antd/es/select';
import {
	MasterInfoEnum,
	OPTION_SELECT_ALL,
	OPTION_SELECT_MASTER,
} from 'constant';

import { compose, prop, sortBy, toLower } from 'ramda';
import { AnyObject } from 'yup';

export interface ISelectMasterInfo extends ISelect {
	type?: MasterInfoEnum;
	label?: string;
	isSelectAll?: boolean;
	hidden?: boolean;
	params?: AnyObject;
	sortByAsc?: string;
	isDetail?: boolean;
	showSearch?: boolean;
	firstOptionText?: string;
}

const SelectMasterInfo = ({
	type,
	isSelectAll,
	hidden,
	value,
	sortByAsc,
	isDetail = false,
	firstOptionText,
	...rest
}: ISelectMasterInfo) => {
	if (hidden) {
		return null;
	}

	const { selectMasterInfo } = useCommonList(type, true, rest.params);

	const formatSelectOption = useCallback(
		(data: DefaultOptionType[] = []) => {
			let newData = [...data];
			if (!rest?.placeholder) {
				newData = [
					isSelectAll
						? OPTION_SELECT_ALL
						: OPTION_SELECT_MASTER(type as MasterInfoEnum, firstOptionText),
					...data,
				];
			}
			return newData;
		},
		[rest.placeholder, isSelectAll, type, firstOptionText]
	);

	const options = useMemo(() => {
		let _options = (
			type ? selectMasterInfo?.data || [] : rest.options
		) as DefaultOptionType[];
		if (sortByAsc) {
			_options = sortBy(compose(toLower, prop(sortByAsc)), _options);
		}
		if (type) {
			_options = formatSelectOption(_options);
		}

		const uniqueOptions = _options.filter(
			(option, index, self) =>
				index ===
				self.findIndex(
					(o) => o.label === option.label && o.value === option.value
				)
		);

		return uniqueOptions;
	}, [type, sortByAsc, formatSelectOption, selectMasterInfo, rest.options]);

	const _value = useMemo(() => {
		if (typeof value !== 'object') {
			return (
				options?.find(
					(e: DefaultOptionType) => String(e.value) === String(value)
				) || value
			);
		}
		return value;
	}, [value, options]);

	return (
		<Select
			options={options}
			value={!isMobile ? _value : _value?.value}
			isDetail={isDetail}
			translationLimit={!!type}
			{...rest}
		/>
	);
};

export default memo(SelectMasterInfo);
